'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { emitListingsEventClient } from '@/lib/telemetry/client';

interface TesterCheckResponse {
  isTester?: boolean;
  error?: string;
}

interface DailyRow {
  id: string;
  caseId: string;
  hearingDate: string;
  causeListType: string | null;
  listingType: string | null;
  bench: string | null;
  judgeNames: string[];
  courtNumber: string | null;
  itemNumber: string | null;
  listNumber: string | null;
  serialNo: number | null;
  hearingTime: string | null;
  hasSnapshot: boolean;
  sourceId: string | null;
  pdfPageNumber: number | null;
  caseNumber: string | null;
  petitioner: string | null;
  respondent: string | null;
  courtName: string | null;
}

interface DailyResponse {
  date: string;
  listings: DailyRow[];
  summary: { total: number; byListingType: Record<string, number> };
}

const SORT_OPTIONS = [
  { value: 'court', label: 'Court No.' },
  { value: 'judge', label: 'Judge' },
  { value: 'client', label: 'Client (petitioner)' },
  { value: 'title', label: 'Case title' },
];

function todayIst(): string {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function DailyCauselistPage() {
  const [date, setDate] = useState(todayIst());
  const [sort, setSort] = useState('court');
  const [courtFilter, setCourtFilter] = useState('');
  const [data, setData] = useState<DailyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testerStatus, setTesterStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/check-listings-tester', { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          setTesterStatus('denied');
          return;
        }
        const j = (await res.json()) as TesterCheckResponse;
        setTesterStatus(j.isTester ? 'allowed' : 'denied');
      } catch {
        if (!cancelled) setTesterStatus('denied');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (testerStatus !== 'allowed') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ date, sort });
        if (courtFilter.trim()) params.set('court', courtFilter.trim());
        const res = await fetch(`/api/dailycauselist?${params.toString()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = (await res.json()) as DailyResponse;
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, sort, courtFilter, testerStatus]);

  useEffect(() => {
    if (data) {
      emitListingsEventClient({
        event: 'dailycauselist_view',
        metadata: { date: data.date, total: data.summary.total, sort, court: courtFilter || null },
      });
    }
  }, [data, sort, courtFilter]);

  const weekDates = useMemo(() => {
    const out: string[] = [];
    for (let i = -3; i <= 3; i++) out.push(addDays(date, i));
    return out;
  }, [date]);

  if (testerStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }
  if (testerStatus === 'denied') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="mx-auto max-w-md rounded-xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">Coming soon</h1>
          <p className="mt-2 text-sm text-gray-600">
            Daily Causelist is in private beta. We&apos;re rolling it out to early testers first.
          </p>
          <Link
            href="/dashboard"
            className="mt-4 inline-block text-sm font-medium text-blue-700 hover:text-blue-800"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Link
          href="/dashboard"
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          ← Back to Dashboard
        </Link>

        <div className="mb-6 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900">Daily Causelist</h1>
          <p className="mt-1 text-sm text-gray-600">
            Every matter you have on the cause list for the selected date.
          </p>
          {data && (
            <p className="mt-2 text-sm font-medium text-gray-700">
              {formatDate(data.date)} · {data.summary.total} matter{data.summary.total === 1 ? '' : 's'}
              {Object.keys(data.summary.byListingType).length > 0 && (
                <span className="ml-2 text-gray-500">
                  ({Object.entries(data.summary.byListingType).map(([k, v]) => `${v} ${k}`).join(', ')})
                </span>
              )}
            </p>
          )}
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
          <button
            type="button"
            onClick={() => setDate(addDays(date, -1))}
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm hover:bg-gray-50"
            aria-label="Previous day"
          >
            ←
          </button>
          <div className="flex flex-1 flex-wrap gap-1">
            {weekDates.map(d => {
              const isActive = d === date;
              const dt = new Date(d + 'T00:00:00Z');
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDate(d)}
                  className={[
                    'flex flex-col items-center rounded-md border px-3 py-1.5 text-xs',
                    isActive
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                  ].join(' ')}
                >
                  <span>{dt.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                  <span className="font-semibold">
                    {dt.getDate()} {dt.toLocaleDateString('en-IN', { month: 'short' })}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setDate(addDays(date, 1))}
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm hover:bg-gray-50"
            aria-label="Next day"
          >
            →
          </button>
          <button
            type="button"
            onClick={() => setDate(todayIst())}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Today
          </button>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
          />
        </div>

        <div className="mb-4 flex flex-wrap gap-3 rounded-lg bg-white p-3 shadow-sm">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Sort
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="rounded-md border border-gray-200 px-2 py-1 text-sm"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <input
            type="text"
            value={courtFilter}
            placeholder="Filter by court name…"
            onChange={e => setCourtFilter(e.target.value)}
            className="flex-1 min-w-[200px] rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          />
          <a
            href={`/api/dailycauselist/export?date=${date}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Export PDF
          </a>
        </div>

        {loading ? (
          <p className="py-12 text-center text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="py-12 text-center text-sm text-red-600">{error}</p>
        ) : !data || data.listings.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
            <p className="text-sm font-medium text-gray-700">No listings for this date.</p>
            <p className="mt-1 text-xs text-gray-500">
              Listings appear here automatically when the court publishes a causelist that names
              one of your cases.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.listings.map(r => (
              <Link
                key={r.id}
                href={`/case/${r.caseId}`}
                className="block rounded-lg border border-gray-200 bg-white p-4 hover:shadow-sm"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">
                      {r.caseNumber ?? 'Case'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {r.petitioner ?? '—'} v. {r.respondent ?? '—'}
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    {r.courtNumber && <div>Court No. {r.courtNumber}</div>}
                    {r.itemNumber && <div>Item {r.itemNumber}</div>}
                    {r.hearingTime && <div>{r.hearingTime}</div>}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                  {r.causeListType && (
                    <span className="rounded-md bg-blue-50 px-2 py-0.5 text-blue-700">
                      {r.causeListType}
                    </span>
                  )}
                  {r.bench && <span>· Bench: {r.bench}</span>}
                  {r.judgeNames.length > 0 && <span>· {r.judgeNames.join(', ')}</span>}
                  {r.courtName && <span>· {r.courtName}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
